import { SlideObject, createObjectId } from '../types';
import { cloneSlideObject } from './objectArrange';

export function groupMembers(objects: SlideObject[], groupId?: string | null): SlideObject[] {
  const id = String(groupId || '').trim();
  if (!id) return [];
  return objects.filter((object) => object.groupId === id);
}

export function expandGroupSelection(objects: SlideObject[], ids: string[]): string[] {
  const wanted = new Set<string>();
  ids.forEach((id) => {
    const object = objects.find((item) => item.id === id);
    if (!object) return;
    if (object.groupId) {
      groupMembers(objects, object.groupId).forEach((member) => wanted.add(member.id));
    } else {
      wanted.add(object.id);
    }
  });
  return objects.filter((object) => wanted.has(object.id)).map((object) => object.id);
}

export function selectionIsolateMode(
  objects: SlideObject[],
  isolatedId: string | null,
  targetId: string,
  additive?: boolean,
): boolean {
  if (additive || !isolatedId) return false;
  const isolated = objects.find((object) => object.id === isolatedId);
  const target = objects.find((object) => object.id === targetId);
  return Boolean(isolated?.groupId && target?.groupId && isolated.groupId === target.groupId);
}

export function nextSelection(
  objects: SlideObject[],
  currentIds: string[],
  targetId: string | null,
  options?: { additive?: boolean; isolate?: boolean },
): string[] {
  if (!targetId) return [];
  if (options?.isolate) return objects.some((object) => object.id === targetId) ? [targetId] : [];
  const expanded = expandGroupSelection(objects, [targetId]);
  if (!options?.additive) return expanded;
  const current = new Set(currentIds);
  const already = expanded.every((id) => current.has(id));
  if (already) {
    expanded.forEach((id) => current.delete(id));
    return objects.filter((object) => current.has(object.id)).map((object) => object.id);
  }
  const merged = new Set([...currentIds, ...expanded]);
  return objects.filter((object) => merged.has(object.id)).map((object) => object.id);
}

/** Keep a multi-selection when starting move/resize on a member already in the set. */
export function gestureTargetIds(
  objects: SlideObject[],
  currentIds: string[],
  targetId: string,
  options?: { additive?: boolean; isolate?: boolean },
): string[] {
  if (!options?.additive && !options?.isolate && currentIds.length > 1 && currentIds.includes(targetId)) {
    return currentIds;
  }
  return nextSelection(objects, currentIds, targetId, options);
}

export function canGroup(ids: string[]): boolean {
  return ids.length >= 2;
}

/** Click-to-write for a lone text; a grouped text selects the group until isolated or double-clicked. */
export function shouldEnterTextEditOnSelect(
  object: Pick<SlideObject, 'id' | 'type' | 'groupId'>,
  objects: SlideObject[],
  options?: { additive?: boolean; isolatedObjectId?: string | null; selectedIds?: string[] },
): boolean {
  if (options?.additive) return false;
  if ((options?.selectedIds?.length || 0) > 1) return false;
  if (object.type !== 'text') return false;
  const members = object.groupId ? groupMembers(objects, object.groupId) : [];
  return members.length < 2 || options?.isolatedObjectId === object.id;
}

export function canUngroup(objects: SlideObject[], ids: string[]): boolean {
  return ids.some((id) => Boolean(objects.find((object) => object.id === id)?.groupId));
}

export function groupObjects(objects: SlideObject[], ids: string[], groupId = createObjectId('grp')): SlideObject[] {
  if (!canGroup(ids)) return objects;
  const selected = new Set(ids);
  return objects.map((object) => (selected.has(object.id) ? { ...object, groupId } : object));
}

export function ungroupObjects(objects: SlideObject[], ids: string[]): SlideObject[] {
  const selected = new Set(ids);
  return objects.map((object) => (selected.has(object.id) ? { ...object, groupId: null } : object));
}

export function canMoveSelection(objects: SlideObject[], ids: string[]): boolean {
  return ids.length > 0 && ids.every((id) => {
    const object = objects.find((item) => item.id === id);
    return Boolean(object && !object.locks.position);
  });
}

export function selectionBounds(objects: SlideObject[], ids: string[]): { x: number; y: number; w: number; h: number } | null {
  const selected = objects.filter((object) => ids.includes(object.id));
  if (!selected.length) return null;
  const left = Math.min(...selected.map((object) => object.x));
  const top = Math.min(...selected.map((object) => object.y));
  const right = Math.max(...selected.map((object) => object.x + object.w));
  const bottom = Math.max(...selected.map((object) => object.y + object.h));
  return { x: left, y: top, w: Math.max(1, right - left), h: Math.max(1, bottom - top) };
}

export function scaleGrouped(
  objects: SlideObject[],
  ids: string[],
  from: { x: number; y: number; w: number; h: number },
  to: { x: number; y: number; w: number; h: number },
): SlideObject[] {
  if (from.w <= 0 || from.h <= 0) return objects;
  const wanted = new Set(ids);
  const sx = to.w / from.w;
  const sy = to.h / from.h;
  const fontScale = (Math.abs(sx) + Math.abs(sy)) / 2;
  return objects.map((object) => {
    if (!wanted.has(object.id)) return object;
    const min = object.style?.shape === 'line' ? 8 : 16;
    const next: SlideObject = {
      ...object,
      x: to.x + (object.x - from.x) * sx,
      y: to.y + (object.y - from.y) * sy,
      w: Math.max(min, object.w * sx),
      h: Math.max(min, object.h * sy),
    };
    if (object.style?.fontSize) {
      next.style = {
        ...object.style,
        fontSize: Math.max(10, Math.round(object.style.fontSize * fontScale)),
      };
    }
    return next;
  });
}

export function moveGrouped(objects: SlideObject[], ids: string[], dx: number, dy: number): SlideObject[] {
  if (!canMoveSelection(objects, ids)) return objects;
  const selected = new Set(ids);
  return objects.map((object) => (
    selected.has(object.id) ? { ...object, x: object.x + dx, y: object.y + dy } : object
  ));
}

export function remapPastedGroupIds(objects: SlideObject[]): SlideObject[] {
  const map = new Map<string, string>();
  return objects.map((object) => {
    const current = object.groupId;
    if (!current) return { ...object, groupId: null };
    let next = map.get(current);
    if (!next) {
      next = createObjectId('grp');
      map.set(current, next);
    }
    return { ...object, groupId: next };
  });
}

export function cloneSelection(
  selected: SlideObject[],
  siblings: SlideObject[],
  offset = { x: 24, y: 24 },
): SlideObject[] {
  const copies: SlideObject[] = [];
  let pool = siblings;
  selected.forEach((object) => {
    const copy = cloneSlideObject(object, pool, offset);
    copies.push(copy);
    pool = [...pool, copy];
  });
  return remapPastedGroupIds(copies);
}
